import React, { useEffect, useState } from 'react';
import topApi from '../../components/api/ToPApi';
import Message from '../../components/ui/Message';
import { errorMessage } from '../../utils/access';
import { CardForm, cardBrandIcon, cardErrors, cardPayload, emptyCard, formatDay, formatMoney, MAX_USERS } from './featureUi';
import { CardFields } from './CardFields';

interface Subscription {
    status: 'PRUEBA' | 'ACTIVA' | 'MOROSA' | 'SUSPENDIDA';
    users: number;
    usersInUse: number;
    card: { last4: string; brand: string | null; expiration: string | null } | null;   // null: aún no registra tarjeta
    trialEndsOn: string | null;
    nextChargeOn: string;
    failedAttempts: number;
    lastError: string | null;
    amount: number;
    currency: string;
    lines: { code: string; name: string; unitPrice: number; quantity: number; amount: number }[];
}

interface MineResponse { subscription: Subscription | null; canManage: boolean; message?: string; }

const STATUS_BADGE: Record<Subscription['status'], { label: string; css: string }> = {
    PRUEBA: { label: 'Periodo de prueba', css: 'bg-info-subtle text-info-emphasis' },
    ACTIVA: { label: 'Al día', css: 'bg-success-subtle text-success-emphasis' },
    MOROSA: { label: 'Cobro pendiente', css: 'bg-warning-subtle text-warning-emphasis' },
    SUSPENDIDA: { label: 'Suspendida', css: 'bg-danger-subtle text-danger-emphasis' },
};

/**
 * Suscripción del comercio (registro autogestionado con cobro a tarjeta): tarjeta, próximo cobro y cantidad de
 * usuarios (retail e inventario se cobran por usuario). El administrador cambia la cantidad; aplica desde el próximo cobro.
 * Sin suscripción no muestra nada.
 */
export const MySubscriptionCard = () => {
    const [data, setData] = useState<MineResponse | null>(null);
    const [users, setUsers] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [editingCard, setEditingCard] = useState(false);
    const [card, setCard] = useState<CardForm>(emptyCard);
    const [cardTouched, setCardTouched] = useState(false);
    const [savingCard, setSavingCard] = useState(false);

    useEffect(() => {
        topApi.get<MineResponse>('/merchant-subscription/mine')
            .then(resp => { setData(resp.data); setUsers(String(resp.data.subscription?.users ?? '')); })
            .catch(() => setData(null));   // sin la tabla o sin permiso: la página sigue sin esta tarjeta
    }, []);

    const sub = data?.subscription;
    if (!sub) return null;

    const cardFormErrors = cardTouched ? cardErrors(card) : {};
    const closeCard = () => { setEditingCard(false); setCard(emptyCard); setCardTouched(false); };

    // Agrega o cambia la tarjeta; si hay un cobro vencido el API lo intenta de inmediato
    const saveCard = async (e: React.FormEvent) => {
        e.preventDefault();
        if (Object.keys(cardErrors(card)).length) { setCardTouched(true); return; }
        setSavingCard(true);
        try {
            const resp = await topApi.put<MineResponse>('/merchant-subscription/mine/card', { card: cardPayload(card) });
            setData(resp.data);
            closeCard();
            setSuccess(resp.data.message ?? 'Tarjeta guardada.');
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setSavingCard(false);
        }
    };

    const count = Number(users);
    const usersInvalid = !Number.isInteger(count) || count < Math.max(1, sub.usersInUse) || count > MAX_USERS;
    const badge = STATUS_BADGE[sub.status] ?? STATUS_BADGE.ACTIVA;

    const saveUsers = async () => {
        if (usersInvalid || count === sub.users) return;
        setSaving(true);
        try {
            const resp = await topApi.put<MineResponse>('/merchant-subscription/mine/users', { users: count });
            setData(resp.data);
            setUsers(String(resp.data.subscription?.users ?? count));
            setSuccess(`Listo: su plan ahora incluye ${count} ${count === 1 ? 'usuario' : 'usuarios'}. El nuevo monto aplica desde el próximo cobro.`);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                    <div>
                        <h6 className="text-muted text-uppercase small mb-1">Suscripción</h6>
                        <span className={`badge rounded-pill ${badge.css}`}>{badge.label}</span>
                    </div>
                    <div className="text-end">
                        <div className="fs-5 fw-semibold">{formatMoney(sub.amount, sub.currency)}<small className="text-muted fw-normal">/mes</small></div>
                        <div className="small text-muted">
                            {sub.status === 'PRUEBA' && sub.trialEndsOn
                                ? <>Prueba hasta el {formatDay(sub.trialEndsOn)}; primer cobro ese día</>
                                : <>Próximo cobro: {formatDay(sub.nextChargeOn)}</>}
                        </div>
                    </div>
                </div>

                {!sub.card && (
                    <div className="alert alert-warning small py-2">
                        <i className="bi bi-exclamation-triangle me-1" />
                        Agregue una tarjeta {sub.status === 'PRUEBA' && sub.trialEndsOn ? <>antes del <strong>{formatDay(sub.trialEndsOn)}</strong>, cuando termina su prueba</> : 'para pagar su suscripción'}.
                        Sin tarjeta no podemos hacer el cobro y la cuenta se suspende después de 3 días.
                        {!data?.canManage && ' Solicítelo al administrador del comercio.'}
                    </div>
                )}

                {sub.status === 'MOROSA' && (
                    <div className="alert alert-warning small py-2">
                        No pudimos cobrar a su tarjeta{sub.lastError ? ` (${sub.lastError})` : ''}. Lo intentaremos de nuevo; si no lo logramos en los
                        próximos días la cuenta será suspendida.
                    </div>
                )}

                <div className="row g-3 align-items-center">
                    <div className="col-md-5">
                        {sub.card ? (
                            <div className="d-flex align-items-center gap-2">
                                <i className={`bi ${cardBrandIcon(sub.card.brand)} fs-4 text-primary`} />
                                <div className="flex-grow-1">
                                    <div className="fw-medium">{sub.card.brand && sub.card.brand !== 'OTRA' ? sub.card.brand : 'Tarjeta'} •••• {sub.card.last4}</div>
                                    {sub.card.expiration && <div className="small text-muted">Vence {sub.card.expiration.slice(0, 2)}/{sub.card.expiration.slice(2)}</div>}
                                </div>
                                {data?.canManage && !editingCard && (
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setEditingCard(true)}>Cambiar</button>
                                )}
                            </div>
                        ) : (
                            <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-credit-card fs-4 text-muted" />
                                <div className="flex-grow-1">
                                    <div className="fw-medium">Sin tarjeta registrada</div>
                                    <div className="small text-muted">La suscripción se cobra a una tarjeta</div>
                                </div>
                                {data?.canManage && !editingCard && (
                                    <button type="button" className="btn btn-sm btn-primary" onClick={() => setEditingCard(true)}>
                                        <i className="bi bi-plus-lg me-1" />Agregar
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="col-md-7">
                        <label htmlFor="sub-users" className="small text-muted mb-1">Usuarios contratados ({sub.usersInUse} en uso)</label>
                        <div className="d-flex gap-2">
                            <input id="sub-users" type="number" min={Math.max(1, sub.usersInUse)} max={MAX_USERS} style={{ maxWidth: 110 }}
                                className={`form-control form-control-sm ${usersInvalid ? 'is-invalid' : ''}`} value={users}
                                disabled={!data?.canManage || saving} onChange={e => setUsers(e.target.value)} />
                            {data?.canManage && (
                                <button type="button" className="btn btn-sm btn-primary" disabled={usersInvalid || count === sub.users || saving} onClick={saveUsers}>
                                    {saving ? <span className="spinner-border spinner-border-sm" /> : 'Actualizar'}
                                </button>
                            )}
                        </div>
                        {usersInvalid && <div className="small text-danger mt-1">Entre {Math.max(1, sub.usersInUse)} y {MAX_USERS} (no menos que los usuarios activos).</div>}
                    </div>
                </div>

                {editingCard && (
                    <form className="border rounded-3 p-3 mt-3" onSubmit={saveCard} noValidate style={{ maxWidth: 520 }}>
                        <h6 className="mb-3">{sub.card ? 'Cambiar tarjeta' : 'Agregar tarjeta'}</h6>
                        <CardFields card={card} onChange={setCard} errors={cardFormErrors} disabled={savingCard} idPrefix="account-card"
                            note={sub.status === 'MOROSA' || (!sub.card && sub.status !== 'PRUEBA')
                                ? `Validamos la tarjeta con su banco y cobramos el pago pendiente de ${formatMoney(sub.amount, sub.currency)}. Solo guardamos los últimos 4 dígitos.`
                                : undefined} />
                        <div className="d-flex gap-2 justify-content-end mt-3">
                            <button type="button" className="btn btn-outline-secondary" disabled={savingCard} onClick={closeCard}>Cancelar</button>
                            <button type="submit" className="btn btn-primary" disabled={savingCard}>
                                {savingCard ? <><span className="spinner-border spinner-border-sm me-2" />Validando...</> : <><i className="bi bi-check2 me-1" />Guardar tarjeta</>}
                            </button>
                        </div>
                    </form>
                )}

                {sub.lines.length > 0 && (
                    <table className="table table-sm small mt-3 mb-0">
                        <tbody>
                            {sub.lines.map(l => (
                                <tr key={l.code}>
                                    <td>{l.name}</td>
                                    <td className="text-muted text-end">{l.quantity > 1 ? `${l.quantity} × ${formatMoney(l.unitPrice, sub.currency)}` : ''}</td>
                                    <td className="text-end" style={{ width: 120 }}>{formatMoney(l.amount, sub.currency)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {error && <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />}
            {success && <Message type="success" title="Suscripción actualizada" message={success} closeDialog={() => setSuccess('')} handleOk={() => setSuccess('')} backgroundColor="rgba(0,0,0,0.4)" />}
        </div>
    );
};

export default MySubscriptionCard;
